/**
 * Discriminated error taxonomy for the API layer.
 *
 * Why a single class with a `kind` discriminator (vs many subclasses)?
 * - Lets consumers exhaust over `error.kind` in switch statements.
 * - Survives minification: `instanceof` + brand-key remain reliable across bundle boundaries.
 * - Plays well with TanStack Query's `throw`-based error contract.
 */
export type ApiErrorKind =
  | 'network'
  | 'timeout'
  | 'unauthorized'
  | 'forbidden'
  | 'not_found'
  | 'conflict'
  | 'rate_limited'
  | 'validation'
  | 'server'
  | 'contract'
  | 'unknown';

export interface ApiErrorOptions {
  readonly status?: number;
  readonly requestId?: string;
  readonly details?: unknown;
  readonly cause?: unknown;
}

export class ApiError extends Error {
  override readonly name = 'ApiError';
  readonly kind: ApiErrorKind;
  readonly status?: number;
  readonly requestId?: string;
  readonly details?: unknown;

  constructor(kind: ApiErrorKind, message: string, opts: ApiErrorOptions = {}) {
    super(message, opts.cause !== undefined ? { cause: opts.cause } : undefined);
    this.kind = kind;
    if (opts.status !== undefined) this.status = opts.status;
    if (opts.requestId !== undefined) this.requestId = opts.requestId;
    if (opts.details !== undefined) this.details = opts.details;
  }

  static network(cause: unknown): ApiError {
    return new ApiError('network', 'Network request failed', { cause });
  }

  static timeout(timeoutMs: number): ApiError {
    return new ApiError('timeout', `Request timed out after ${String(timeoutMs)}ms`);
  }

  static contract(zodIssues: unknown): ApiError {
    return new ApiError('contract', 'API response did not match expected schema', {
      details: zodIssues,
    });
  }

  static fromResponse(res: Response, body: unknown): ApiError {
    const requestId = res.headers.get('x-request-id') ?? undefined;
    const kind = ApiError.kindFromStatus(res.status);
    const message =
      typeof body === 'object' && body !== null && 'message' in body && typeof body.message === 'string'
        ? body.message
        : `HTTP ${String(res.status)} ${res.statusText}`;
    const opts: ApiErrorOptions = { status: res.status, details: body };
    if (requestId !== undefined) {
      return new ApiError(kind, message, { ...opts, requestId });
    }
    return new ApiError(kind, message, opts);
  }

  private static kindFromStatus(status: number): ApiErrorKind {
    if (status === 401) return 'unauthorized';
    if (status === 403) return 'forbidden';
    if (status === 404) return 'not_found';
    if (status === 409) return 'conflict';
    if (status === 422) return 'validation';
    if (status === 429) return 'rate_limited';
    if (status >= 500) return 'server';
    if (status >= 400) return 'validation';
    return 'unknown';
  }
}

export const isApiError = (e: unknown): e is ApiError => e instanceof ApiError;
